import { PDFDocument, PDFName, PDFString, rgb, type PDFFont, type PDFImage, type PDFPage } from "pdf-lib";
import type { Block, Border, BorderSet, DocxDoc, Inline, Para, ParaProps, RunProps, Section, Table } from "./docxModel";
import { blocksText } from "./docxModel";
import { familyForFontName, PdfFontSet, RasterFont, RTL_RE, replacedCharsMessage, type FallbackLoader, type RasterEnv, type FontRun } from "./pdfFonts";
import { stampOutputMetadata } from "./pdfMetadata";

/**
 * Draws a document read by docxRead.ts into a PDF: real text (selectable, searchable) in the built-in
 * font family closest to the Word font, with bold, italic, colour, underline, lists, tables with
 * borders and shading, pictures, links, headers / footers with page numbers, page size and margins
 * per section, page breaks, and widow / orphan / keep-with-next control. The words wrap and the
 * pages break with the PDF fonts' own widths, so line breaks can differ a little from Word's.
 */

// ---------------------------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------------------------

interface TextStyle {
  runs: FontRun[];
  size: number;
  color: [number, number, number];
  underline: boolean;
  strike: boolean;
  background?: [number, number, number];
  link?: string;
  raise: number;
}

type Atom =
  | ({ k: "word"; w: number; breakBefore: boolean } & TextStyle)
  | { k: "space"; w: number; size: number; background?: [number, number, number]; link?: string }
  | { k: "tab"; size: number; implicit?: boolean }
  | { k: "br"; size: number }
  | { k: "pagebreak"; size: number }
  | { k: "image"; w: number; h: number; image: PDFImage };

interface Cluster {
  kind: "text" | "tab" | "br" | "pb" | "image";
  atoms: Atom[];
  width: number;
  /** Width of the spaces after this cluster (a break opportunity). */
  spaceW: number;
  spaces: Atom[];
  implicit?: boolean;
}

interface Placed {
  atom: Atom;
  x: number;
  /** Number of justification gaps before this item. */
  gaps: number;
}

interface Line {
  items: Placed[];
  startX: number;
  /** Width of the content, without trailing spaces. */
  width: number;
  avail: number;
  height: number;
  baseline: number;
  gapCount: number;
  endsHard: boolean;
  pageBreakAfter: boolean;
  hasTab: boolean;
}

interface ParaLayout {
  kind: "p";
  lines: Line[];
  props: ParaProps;
  before: number;
  after: number;
  width: number;
  styleId?: string;
}

interface CellBox {
  col: number;
  span: number;
  x: number;
  w: number;
  content: Layout[];
  contentH: number;
  shade?: string;
  borders: BorderSet;
  vAlign: "top" | "center" | "bottom";
  margin: { left: number; right: number; top: number; bottom: number };
  rowSpan: number;
  hidden: boolean;
}

interface RowLayout {
  height: number;
  cells: CellBox[];
  header: boolean;
  cantSplit: boolean;
}

interface TableLayout {
  kind: "t";
  table: Table;
  rows: RowLayout[];
  width: number;
  x: number;
  cols: number[];
}

type Layout = ParaLayout | TableLayout;

export interface DocxPdfHooks {
  isCurrent?: () => boolean;
  progress?: (fraction: number) => void;
}

export interface DocxPdfOptions {
  fallbackLoader?: FallbackLoader;
  /** Draws text no font can (default: the browser canvas; null or a test double to override). */
  rasterEnv?: RasterEnv | null;
  fallbackTitle: string;
}

export interface DocxPdfResult {
  bytes: Uint8Array;
  pages: number;
  /** Things the person should know (characters that could not be drawn, content not included). */
  warnings: string[];
  replacedChars: number;
}

export class DocxPdfCancelled extends Error {
  constructor() {
    super("cancelled");
  }
}

// ---------------------------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------------------------

const LINE_FACTOR = 1.17;
const DEFAULT_SIZE = 11;
const DEFAULT_TAB = 36;
const EPS = 0.01;

const toRgb = (hex: string | undefined, fallback: [number, number, number] = [0, 0, 0]): [number, number, number] => {
  if (!hex || !/^[0-9a-fA-F]{6}$/.test(hex)) return fallback;
  return [parseInt(hex.slice(0, 2), 16) / 255, parseInt(hex.slice(2, 4), 16) / 255, parseInt(hex.slice(4, 6), 16) / 255];
};

function collectInlines(blocks: Block[], out: Inline[] = []): Inline[] {
  for (const b of blocks) {
    if (b.kind === "p") out.push(...b.inlines);
    else for (const r of b.rows) for (const c of r.cells) collectInlines(c.blocks, out);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// the writer
// ---------------------------------------------------------------------------------------------

class Writer {
  pdf!: PDFDocument;
  fonts!: PdfFontSet;
  images = new Map<Uint8Array, PDFImage>();
  imageFailures = 0;
  pages: { page: PDFPage; section: Section; index: number; links: { x: number; y: number; w: number; h: number; url: string }[] }[] = [];
  total = 1;
  // pagination state
  cur!: { page: PDFPage; section: Section; index: number; links: { x: number; y: number; w: number; h: number; url: string }[] };
  y = 0;
  top = 0;
  bottom = 0;
  sec!: Section;
  prevPara?: { styleId?: string; contextual: boolean; after: number; pageNo: number };
  atTop = true;
  pageNo = 0;
  counting = false;

  constructor(private doc: DocxDoc, private opts: DocxPdfOptions, private hooks: DocxPdfHooks) {}

  alive() {
    if (this.hooks.isCurrent && !this.hooks.isCurrent()) throw new DocxPdfCancelled();
  }

  // ---- text -> atoms ------------------------------------------------------------------------

  style(p: RunProps, text: string, link?: string): TextStyle {
    const family = familyForFontName(p.font);
    let size = p.sizePt ?? DEFAULT_SIZE;
    let raise = 0;
    if (p.vert) {
      raise = p.vert === "sup" ? size * 0.35 : -size * 0.12;
      size *= 0.65;
    }
    if (p.smallCaps && !p.caps) size *= 0.85;
    const t = p.caps || p.smallCaps ? text.toUpperCase() : text;
    return {
      runs: this.fonts.runs(t, family, !!p.bold, !!p.italic),
      size,
      color: toRgb(p.color),
      underline: !!p.underline,
      strike: !!p.strike,
      background: p.background ? toRgb(p.background) : undefined,
      link,
      raise,
    };
  }

  widthOf(runs: FontRun[], size: number): number {
    let w = 0;
    for (const r of runs) w += r.font.widthOfTextAtSize(r.text, size);
    return w;
  }

  atomsFor(inlines: Inline[], fieldPage: number): Atom[] {
    const atoms: Atom[] = [];
    let prevWasSpace = true;
    const addText = (text: string, p: RunProps, link?: string) => {
      const base = this.style(p, "", link);
      const sizeForSpace = base.size;
      const spaceW = this.fonts.standard(familyForFontName(p.font), !!p.bold, !!p.italic).widthOfTextAtSize(" ", sizeForSpace);
      const parts = text.split(/( +)/);
      for (const part of parts) {
        if (!part) continue;
        if (part[0] === " ") {
          for (let i = 0; i < part.length; i++) atoms.push({ k: "space", w: spaceW, size: sizeForSpace, background: base.background, link });
          prevWasSpace = true;
          continue;
        }
        // Break opportunities after hyphens and dashes.
        const pieces = part.split(/(?<=[-–—])(?=[^-–—])/);
        pieces.forEach((piece, idx) => {
          const st = this.style(p, piece, link);
          atoms.push({ k: "word", w: this.widthOf(st.runs, st.size), breakBefore: idx > 0 || prevWasSpace, ...st });
          prevWasSpace = false;
        });
      }
    };
    for (const i of inlines) {
      switch (i.t) {
        case "text": addText(i.text.replace(/[\r\n]+/g, " "), i.p, i.link); break;
        case "field": addText(i.kind === "PAGE" ? String(fieldPage) : String(this.total), i.p); break;
        case "footnoteRef": addText(i.label, i.p); break;
        case "tab": atoms.push({ k: "tab", size: i.p.sizePt ?? DEFAULT_SIZE }); prevWasSpace = true; break;
        case "br": atoms.push({ k: "br", size: i.p.sizePt ?? DEFAULT_SIZE }); prevWasSpace = true; break;
        case "pagebreak": atoms.push({ k: "pagebreak", size: DEFAULT_SIZE }); prevWasSpace = true; break;
        case "image": {
          const image = this.images.get(i.data);
          if (image) atoms.push({ k: "image", w: i.wPt, h: i.hPt, image });
          prevWasSpace = false;
          break;
        }
      }
    }
    return atoms;
  }

  // ---- paragraphs -> lines -------------------------------------------------------------------

  layoutPara(p: Para, width: number, fieldPage: number): ParaLayout {
    const props = p.props;
    const inlines: Inline[] = [];
    if (p.list) {
      inlines.push({ t: "text", text: p.list.text, p: p.list.p });
      inlines.push({ t: "tab", p: p.list.p });
    }
    inlines.push(...p.inlines);
    const atoms = this.atomsFor(inlines, fieldPage);
    if (p.list) {
      const tab = atoms.find((a) => a.k === "tab");
      if (tab && tab.k === "tab") tab.implicit = true;
    }
    const lines = this.breakLines(atoms, props, width, p.base.sizePt ?? DEFAULT_SIZE);
    return { kind: "p", lines, props, before: props.beforePt, after: props.afterPt, width, styleId: p.styleId };
  }

  clusters(atoms: Atom[]): Cluster[] {
    const out: Cluster[] = [];
    let cur: Cluster | null = null;
    let closed = false;
    const push = () => { if (cur) out.push(cur); cur = null; };
    for (const a of atoms) {
      if (a.k === "space") {
        if (!cur) { cur = { kind: "text", atoms: [], width: 0, spaceW: 0, spaces: [] }; }
        cur.spaceW += a.w;
        cur.spaces.push(a);
        closed = true;
      } else if (a.k === "word") {
        if (!cur || closed || a.breakBefore) { push(); cur = { kind: "text", atoms: [], width: 0, spaceW: 0, spaces: [] }; closed = false; }
        cur!.atoms.push(a);
        cur!.width += a.w;
      } else if (a.k === "image") {
        push();
        out.push({ kind: "image", atoms: [a], width: a.w, spaceW: 0, spaces: [] });
        closed = false;
      } else {
        push();
        out.push({ kind: a.k === "tab" ? "tab" : a.k === "br" ? "br" : "pb", atoms: [a], width: 0, spaceW: 0, spaces: [], implicit: a.k === "tab" ? a.implicit : undefined });
        closed = false;
      }
    }
    push();
    return out;
  }

  breakLines(atoms: Atom[], props: ParaProps, width: number, markSize: number): Line[] {
    const clusters = this.clusters(atoms);
    const lines: Line[] = [];
    const rightLimit = (first: boolean) => width - props.rightPt - (first ? props.leftPt + props.firstPt : props.leftPt);
    let first = true;
    let startX = props.leftPt + props.firstPt;
    let avail = rightLimit(true);
    let items: Placed[] = [];
    let x = 0;
    let gaps = 0;
    let hasTab = false;

    const finish = (endsHard: boolean, pageBreakAfter = false) => {
      // content width excludes trailing spaces
      let contentW = x;
      const last = items[items.length - 1];
      void last;
      let h = 0, asc = 0, size = 0;
      for (const it of items) {
        const a = it.atom;
        if (a.k === "word") { size = Math.max(size, a.size); }
        else if (a.k === "space" || a.k === "tab" || a.k === "br" || a.k === "pagebreak") size = Math.max(size, a.size);
        else if (a.k === "image") { asc = Math.max(asc, a.h); }
      }
      if (items.length === 0) size = markSize;
      if (size === 0) size = markSize;
      const natural = size * LINE_FACTOR;
      let lineH = natural;
      const lr = props.line;
      if (lr.rule === "auto") lineH = natural * lr.value;
      else if (lr.rule === "exact") lineH = lr.value;
      else lineH = Math.max(natural, lr.value);
      let baseline = lineH - size * 0.22;
      if (asc > 0) {
        const need = asc + size * 0.22;
        if (need > lineH && lr.rule !== "exact") { lineH = need; baseline = asc; }
        else baseline = Math.max(baseline, asc);
      }
      h = lineH;
      lines.push({ items, startX, width: contentW, avail, height: h, baseline, gapCount: gaps, endsHard, pageBreakAfter, hasTab });
      items = [];
      x = 0;
      gaps = 0;
      hasTab = false;
      if (first) { first = false; }
      startX = props.leftPt;
      avail = rightLimit(false);
    };

    const stopFor = (pos: number): { pos: number; align: "left" | "center" | "right" | "decimal" } => {
      const stops = [...props.tabs];
      if (props.firstPt < 0 || true) stops.push({ posPt: props.leftPt, align: "left" });
      stops.sort((a, b) => a.posPt - b.posPt);
      for (const s of stops) if (s.posPt > pos + 0.5) return { pos: s.posPt, align: s.align };
      const last = stops.length ? stops[stops.length - 1].posPt : 0;
      const base = Math.max(last, 0);
      const n = Math.floor((pos - base) / DEFAULT_TAB) + 1;
      return { pos: base + n * DEFAULT_TAB, align: "left" };
    };

    const placeCluster = (c: Cluster, withSpace: boolean) => {
      for (const a of c.atoms) {
        const w = a.k === "word" ? a.w : a.k === "image" ? a.w : 0;
        items.push({ atom: a, x, gaps });
        x += w;
      }
      if (withSpace && c.spaces.length) {
        for (const s of c.spaces) { items.push({ atom: s, x, gaps }); x += s.k === "space" ? s.w : 0; }
        gaps++;
      }
    };

    for (let ci = 0; ci < clusters.length; ci++) {
      const c = clusters[ci];
      if (c.kind === "br") { items.push({ atom: c.atoms[0], x, gaps }); finish(true); continue; }
      if (c.kind === "pb") { finish(true, true); continue; }
      if (c.kind === "tab") {
        const pos = startX + x;
        const stop = c.implicit && props.firstPt < 0 && pos < props.leftPt + EPS ? { pos: props.leftPt, align: "left" as const } : stopFor(pos);
        let target = stop.pos;
        if (stop.align !== "left") {
          let ahead = 0;
          for (let j = ci + 1; j < clusters.length && clusters[j].kind !== "tab" && clusters[j].kind !== "br" && clusters[j].kind !== "pb"; j++) {
            ahead += clusters[j].width + (clusters[j + 1] && clusters[j + 1].kind !== "tab" ? clusters[j].spaceW : 0);
          }
          if (stop.align === "right") target = stop.pos - ahead;
          else if (stop.align === "center") target = stop.pos - ahead / 2;
          else target = stop.pos - ahead / 2;
          if (target < pos) target = pos;
        }
        items.push({ atom: c.atoms[0], x, gaps });
        x = Math.min(target - startX, avail + 200);
        hasTab = true;
        continue;
      }
      // text or image cluster
      if (x + c.width > avail + EPS && items.some((it) => it.atom.k === "word" || it.atom.k === "image")) {
        finish(false);
      }
      if (c.width > avail + EPS && c.kind === "text") {
        // A word wider than the line (a long URL): split it by characters.
        const pieces = this.splitLongCluster(c, Math.max(avail, 20));
        pieces.forEach((pc, pi) => {
          if (pi > 0) finish(false);
          placeCluster(pc, pi === pieces.length - 1);
        });
        continue;
      }
      placeCluster(c, true);
    }
    // the last line always exists (an empty paragraph is one empty line)
    finish(false);
    const lastLine = lines[lines.length - 1];
    lastLine.endsHard = true; // the paragraph end counts as a hard break (no justification)
    // trim: a pagebreak at the very end leaves the (empty) last line on the next page, as in Word
    return lines;
  }

  splitLongCluster(c: Cluster, avail: number): Cluster[] {
    const out: Cluster[] = [];
    let cur: Cluster = { kind: "text", atoms: [], width: 0, spaceW: 0, spaces: [] };
    for (const a of c.atoms) {
      if (a.k !== "word") continue;
      for (const run of a.runs) {
        for (const ch of Array.from(run.text)) {
          const cw = run.font.widthOfTextAtSize(ch, a.size);
          if (cur.width + cw > avail && cur.atoms.length) {
            out.push(cur);
            cur = { kind: "text", atoms: [], width: 0, spaceW: 0, spaces: [] };
          }
          const last = cur.atoms[cur.atoms.length - 1];
          if (last && last.k === "word" && last.runs[last.runs.length - 1].font === run.font && last.color === a.color) {
            last.runs[last.runs.length - 1].text += ch;
            last.w += cw;
          } else {
            cur.atoms.push({ ...a, runs: [{ text: ch, font: run.font }], w: cw, breakBefore: false });
          }
          cur.width += cw;
        }
      }
    }
    cur.spaceW = c.spaceW;
    cur.spaces = c.spaces;
    out.push(cur);
    return out;
  }

  // ---- drawing primitives -------------------------------------------------------------------

  drawLine(page: PDFPage, pageH: number, links: { x: number; y: number; w: number; h: number; url: string }[], line: Line, align: ParaProps["align"], originX: number, yTop: number) {
    let shift = 0;
    let extra = 0;
    const free = line.avail - line.width;
    if (align === "center") shift = free / 2;
    else if (align === "right") shift = free;
    else if (align === "justify" && !line.endsHard && !line.hasTab && line.gapCount > 0 && free > 0) extra = free / line.gapCount;
    const baseY = yTop + line.baseline;
    const xs = line.items.map((it) => originX + line.startX + shift + it.x + it.gaps * extra);
    // Right-to-left text (Arabic, Hebrew): words are laid out in reading order, so each run of such
    // words (and the spaces between them) is mirrored within the space it occupies.
    const isRtl = (a: Atom) => a.k === "word" && a.runs.some((r) => r.font instanceof RasterFont && RTL_RE.test(r.text));
    const widthOfAtom = (a: Atom) => (a.k === "word" || a.k === "space" || a.k === "image" ? a.w : 0);
    for (let i = 0; i < line.items.length; i++) {
      if (!isRtl(line.items[i].atom)) continue;
      let j = i;
      for (let k = i + 1; k < line.items.length; k++) {
        const at = line.items[k].atom;
        if (isRtl(at)) j = k;
        else if (at.k !== "space") break;
      }
      const left = xs[i];
      const right = xs[j] + widthOfAtom(line.items[j].atom);
      for (let k = i; k <= j; k++) xs[k] = left + right - (xs[k] + widthOfAtom(line.items[k].atom));
      i = j;
    }
    for (let ii = 0; ii < line.items.length; ii++) {
      const it = line.items[ii];
      const a = it.atom;
      const x = xs[ii];
      if (a.k === "word") {
        let cx = x;
        const y = pageH - (baseY - a.raise);
        if (a.background) page.drawRectangle({ x, y: y - a.size * 0.22, width: a.w, height: a.size * 1.1, color: rgb(...a.background) });
        for (const r of a.runs) {
          if (!r.text) continue;
          this.fonts.drawRun(page, r, { x: cx, y, size: a.size, color: a.color });
          cx += r.font.widthOfTextAtSize(r.text, a.size);
        }
        if (a.underline) page.drawLine({ start: { x, y: y - a.size * 0.12 }, end: { x: x + a.w, y: y - a.size * 0.12 }, thickness: Math.max(0.4, a.size * 0.05), color: rgb(...a.color) });
        if (a.strike) page.drawLine({ start: { x, y: y + a.size * 0.28 }, end: { x: x + a.w, y: y + a.size * 0.28 }, thickness: Math.max(0.4, a.size * 0.05), color: rgb(...a.color) });
        if (a.link) links.push({ x, y: y - a.size * 0.22, w: a.w, h: a.size * 1.1, url: a.link });
      } else if (a.k === "space") {
        if (a.background) page.drawRectangle({ x, y: pageH - baseY - a.size * 0.22, width: a.w + (it.gaps >= 0 ? 0 : 0), height: a.size * 1.1, color: rgb(...a.background) });
      } else if (a.k === "image") {
        page.drawImage(a.image, { x, y: pageH - baseY, width: a.w, height: a.h });
      }
    }
  }

  borderLine(page: PDFPage, pageH: number, b: Border, x1: number, y1: number, x2: number, y2: number) {
    const color = rgb(...toRgb(b.color));
    const t = Math.max(0.25, b.sizePt);
    const dash = b.style === "dashed" ? [t * 3, t * 2] : b.style === "dotted" ? [t, t * 1.5] : undefined;
    const horizontal = Math.abs(y1 - y2) < 0.01;
    const draw = (off: number, th: number) =>
      page.drawLine({
        start: { x: x1 + (horizontal ? 0 : off), y: pageH - (y1 + (horizontal ? off : 0)) },
        end: { x: x2 + (horizontal ? 0 : off), y: pageH - (y2 + (horizontal ? off : 0)) },
        thickness: th, color, dashArray: dash,
      });
    if (b.style === "double") { draw(-t, t * 0.8); draw(t, t * 0.8); } else draw(0, t);
  }

  // ---- tables -------------------------------------------------------------------------------

  layoutTable(table: Table, width: number, fieldPage: number): TableLayout {
    const sum = table.grid.reduce((a, b) => a + b, 0) || 1;
    let target = table.widthPt ?? (table.widthFraction ? table.widthFraction * width : sum);
    const maxW = width - table.indentPt;
    if (!table.widthPt && !table.widthFraction && sum > maxW) target = maxW;
    if (target > maxW) target = maxW;
    const scale = target / sum;
    const cols = table.grid.map((g) => g * scale);
    const xs: number[] = [0];
    cols.forEach((c, i) => xs.push(xs[i] + c));
    const tableW = xs[xs.length - 1];
    let x0 = table.indentPt;
    if (table.align === "center") x0 = (width - tableW) / 2;
    else if (table.align === "right") x0 = width - tableW;

    const rows: RowLayout[] = [];
    table.rows.forEach((row) => {
      let col = 0;
      const cells: CellBox[] = [];
      for (const cell of row.cells) {
        const span = Math.min(cell.span, Math.max(1, cols.length - col));
        const w = xs[Math.min(col + span, cols.length)] - xs[col];
        const margin = {
          left: cell.margins?.left ?? table.margins.left,
          right: cell.margins?.right ?? table.margins.right,
          top: cell.margins?.top ?? table.margins.top,
          bottom: cell.margins?.bottom ?? table.margins.bottom,
        };
        const inner = Math.max(10, w - margin.left - margin.right);
        const hidden = cell.vMerge === "continue";
        const content = hidden ? [] : this.layoutBlocks(cell.blocks, inner, fieldPage);
        const contentH = content.reduce((s, l) => s + this.heightOf(l, true), 0);
        cells.push({ col, span, x: xs[col], w, content, contentH: hidden ? 0 : contentH + margin.top + margin.bottom, shade: cell.shade, borders: cell.borders, vAlign: cell.vAlign, margin, rowSpan: 1, hidden });
        col += span;
      }
      let h = Math.max(0, ...cells.filter((c) => !c.hidden).map((c) => c.contentH));
      if (row.heightPt && row.heightRule === "exact") h = row.heightPt;
      else if (row.heightPt) h = Math.max(h, row.heightPt);
      if (h === 0) h = 12;
      rows.push({ height: h, cells, header: row.header, cantSplit: row.cantSplit });
    });
    // vertical merges: a restart cell spans the rows below that continue it
    rows.forEach((row, ri) => {
      row.cells.forEach((cell) => {
        const src = table.rows[ri].cells[row.cells.indexOf(cell)];
        if (src.vMerge !== "restart") return;
        let n = 1;
        for (let r = ri + 1; r < rows.length; r++) {
          const below = rows[r].cells.find((c) => c.col === cell.col);
          if (below && below.hidden) n++; else break;
        }
        cell.rowSpan = n;
        const have = rows.slice(ri, ri + n).reduce((s, r) => s + r.height, 0);
        if (cell.contentH > have) rows[ri + n - 1].height += cell.contentH - have;
      });
    });
    return { kind: "t", table, rows, width: tableW, x: x0, cols: xs };
  }

  drawRow(page: PDFPage, pageH: number, links: { x: number; y: number; w: number; h: number; url: string }[], t: TableLayout, ri: number, originX: number, yTop: number, rowsBelow?: RowLayout[]) {
    const row = t.rows[ri];
    const tb = t.table.borders;
    const lastRow = ri === t.rows.length - 1;
    row.cells.forEach((cell, ci) => {
      if (cell.hidden) return;
      let h = row.height;
      for (let k = 1; k < cell.rowSpan; k++) h += (rowsBelow ?? t.rows.slice(ri + 1))[k - 1]?.height ?? 0;
      const x = originX + t.x + cell.x;
      if (cell.shade) page.drawRectangle({ x, y: pageH - (yTop + h), width: cell.w, height: h, color: rgb(...toRgb(cell.shade)) });
      // content
      const free = h - cell.contentH;
      let cy = yTop + cell.margin.top + (cell.vAlign === "center" ? free / 2 : cell.vAlign === "bottom" ? free : 0);
      for (const l of cell.content) {
        this.drawLayout(page, pageH, links, l, x + cell.margin.left, cy);
        cy += this.heightOf(l, true);
      }
      // borders
      const first = ri === 0;
      const firstCol = cell.col === 0;
      const lastCol = cell.col + cell.span >= t.cols.length - 1;
      const ov = (k: keyof BorderSet) => cell.borders[k];
      const pick = (k: keyof BorderSet, fallback: Border | undefined) => (ov(k) ? (ov(k)!.style === "none" ? undefined : ov(k)) : fallback);
      const top = pick("top", first ? tb.top : tb.insideH);
      const left = pick("left", firstCol ? tb.left : tb.insideV);
      const bottom = pick("bottom", lastRow || cell.rowSpan > 1 && ri + cell.rowSpan >= t.rows.length ? tb.bottom : undefined);
      const right = pick("right", lastCol ? tb.right : undefined);
      if (top) this.borderLine(page, pageH, top, x, yTop, x + cell.w, yTop);
      if (left) this.borderLine(page, pageH, left, x, yTop, x, yTop + h);
      if (bottom) this.borderLine(page, pageH, bottom, x, yTop + h, x + cell.w, yTop + h);
      if (right) this.borderLine(page, pageH, right, x + cell.w, yTop, x + cell.w, yTop + h);
      void ci;
    });
  }

  // ---- generic layouts ----------------------------------------------------------------------

  layoutBlocks(blocks: Block[], width: number, fieldPage: number): Layout[] {
    return blocks.map((b) => (b.kind === "p" ? this.layoutPara(b, width, fieldPage) : this.layoutTable(b, width, fieldPage)));
  }

  /** Height of a layout when it is laid out as one unit (cells, headers, footers). */
  heightOf(l: Layout, includeSpacing: boolean): number {
    if (l.kind === "p") {
      const lines = l.lines.reduce((s, ln) => s + ln.height, 0);
      return lines + (includeSpacing ? l.before + l.after : 0);
    }
    return l.rows.reduce((s, r) => s + r.height, 0);
  }

  drawLayout(page: PDFPage, pageH: number, links: { x: number; y: number; w: number; h: number; url: string }[], l: Layout, originX: number, yTop: number) {
    if (l.kind === "p") {
      let y = yTop + l.before;
      this.paraDecor(page, pageH, l, originX, y, l.lines.reduce((s, ln) => s + ln.height, 0), true, true);
      for (const ln of l.lines) {
        this.drawLine(page, pageH, links, ln, l.props.align, originX, y);
        y += ln.height;
      }
    } else {
      let y = yTop;
      l.rows.forEach((r, ri) => {
        this.drawRow(page, pageH, links, l, ri, originX, y);
        y += r.height;
      });
    }
  }

  paraDecor(page: PDFPage, pageH: number, l: ParaLayout, originX: number, yTop: number, height: number, topEdge: boolean, bottomEdge: boolean) {
    const x = originX + l.props.leftPt;
    const w = l.width - l.props.leftPt - l.props.rightPt;
    if (l.props.shade) page.drawRectangle({ x, y: pageH - (yTop + height), width: w, height, color: rgb(...toRgb(l.props.shade)) });
    const b = l.props.borders;
    if (b.top && topEdge) this.borderLine(page, pageH, b.top, x, yTop, x + w, yTop);
    if (b.bottom && bottomEdge) this.borderLine(page, pageH, b.bottom, x, yTop + height, x + w, yTop + height);
    if (b.left) this.borderLine(page, pageH, b.left, x, yTop, x, yTop + height);
    if (b.right) this.borderLine(page, pageH, b.right, x + w, yTop, x + w, yTop + height);
  }

  // ---- pagination ---------------------------------------------------------------------------

  newPage(section: Section) {
    const page = this.pdf.addPage([section.widthPt, section.heightPt]);
    this.pageNo++;
    this.cur = { page, section, index: this.pageNo, links: [] };
    this.pages.push(this.cur);
    this.sec = section;
    const { top, bottom } = this.bodyBounds(section, this.pageNo === 1 && section.titlePg);
    this.top = top;
    this.bottom = section.heightPt - bottom;
    this.y = this.top;
    this.atTop = true;
  }

  headerLayouts(section: Section, pageNo: number): { header: Layout[]; footer: Layout[] } {
    const first = section.titlePg && this.isFirstOfSection(section, pageNo);
    const hb = (first ? section.headerFirst : section.header) ?? [];
    const fb = (first ? section.footerFirst : section.footer) ?? [];
    const w = section.widthPt - section.marginLeft - section.marginRight;
    return { header: this.layoutBlocks(hb, w, pageNo), footer: this.layoutBlocks(fb, w, pageNo) };
  }

  private sectionFirstPage = new Map<Section, number>();
  isFirstOfSection(section: Section, pageNo: number) {
    return this.sectionFirstPage.get(section) === pageNo;
  }

  bodyBounds(section: Section, first: boolean): { top: number; bottom: number } {
    const hb = (first ? section.headerFirst : section.header) ?? [];
    const fb = (first ? section.footerFirst : section.footer) ?? [];
    const w = section.widthPt - section.marginLeft - section.marginRight;
    const hH = hb.length ? this.layoutBlocks(hb, w, 1).reduce((s, l) => s + this.heightOf(l, true), 0) : 0;
    const fH = fb.length ? this.layoutBlocks(fb, w, 1).reduce((s, l) => s + this.heightOf(l, true), 0) : 0;
    return {
      top: Math.max(section.marginTop, hb.length ? section.headerDist + hH + 6 : 0),
      bottom: Math.max(section.marginBottom, fb.length ? section.footerDist + fH + 6 : 0),
    };
  }

  contentWidth() {
    return this.sec.widthPt - this.sec.marginLeft - this.sec.marginRight;
  }

  remaining() {
    return this.bottom - this.y;
  }

  placePara(p: Para) {
    const w = this.contentWidth();
    let l = this.layoutPara(p, w, this.pageNo);
    const contextual = this.prevPara && this.prevPara.contextual && l.props.contextualSpacing && this.prevPara.styleId === l.styleId;
    if (contextual) {
      // Word: "don't add space between paragraphs of the same style" drops the gap on both sides (WP-11).
      l.before = 0;
      if (this.prevPara!.pageNo === this.pageNo) this.y -= this.prevPara!.after;
    }
    if (l.props.pageBreakBefore && !this.atTop) this.newPage(this.sec);
    let before = this.atTop && this.pageNo > 1 ? 0 : l.before;
    if (this.atTop && this.pageNo === 1 && this.pagesOfSection === 0) before = l.before;
    const total = l.lines.reduce((s, ln) => s + ln.height, 0);
    const pageH = this.bottom - this.top;
    // keep lines together when the paragraph fits on one page
    if ((l.props.keepLines || (!l.props.widowControl && false)) && total + before > this.remaining() && total <= pageH && !this.atTop) {
      this.newPage(this.sec);
      l = this.layoutPara(p, this.contentWidth(), this.pageNo);
      before = 0;
    }
    this.y += before;
    this.atTop = false;
    let i = 0;
    const n = l.lines.length;
    while (i < n) {
      // how many lines fit here
      let fit = 0;
      let used = 0;
      while (i + fit < n && used + l.lines[i + fit].height <= this.remaining() + EPS) {
        used += l.lines[i + fit].height;
        fit++;
        if (l.lines[i + fit - 1].pageBreakAfter) break; // a manual page break ends the page right here
      }
      const brokeByHand = fit > 0 && l.lines[i + fit - 1].pageBreakAfter;
      if (fit < n - i && !brokeByHand) {
        // widow / orphan control: never leave one line alone on either page
        if (l.props.widowControl && n >= 4) {
          if (i === 0 && fit === 1) fit = 0;
          else if (n - (i + fit) === 1 && fit >= 2) fit -= 1;
        } else if (l.props.widowControl && n >= 2 && n < 4 && i === 0 && fit >= 1 && fit < n) fit = 0;
        if (fit === 0) {
          if (this.y <= this.top + EPS || (this.remaining() >= this.bottom - this.top - EPS)) {
            fit = 1; // nothing smaller fits on an empty page: place one line anyway
          } else {
            this.newPage(this.sec);
            continue;
          }
        }
      }
      const slice = l.lines.slice(i, i + fit);
      const sliceH = slice.reduce((s, ln) => s + ln.height, 0);
      this.paraDecor(this.cur.page, this.sec.heightPt, l, this.sec.marginLeft, this.y, sliceH, i === 0, i + fit >= n);
      let y = this.y;
      for (const ln of slice) {
        this.drawLine(this.cur.page, this.sec.heightPt, this.cur.links, ln, l.props.align, this.sec.marginLeft, y);
        y += ln.height;
      }
      this.y = y;
      this.atTop = false;
      i += fit;
      const last = slice[slice.length - 1];
      if (last.pageBreakAfter) { this.newPage(this.sec); }
      else if (i < n) this.newPage(this.sec);
    }
    this.y += l.after;
    this.prevPara = { styleId: l.styleId, contextual: l.props.contextualSpacing, after: l.after, pageNo: this.pageNo };
  }

  private pagesOfSection = 0;

  placeTable(t: Table) {
    const w = this.contentWidth();
    const lay = this.layoutTable(t, w, this.pageNo);
    const pageH = this.bottom - this.top;
    const headerRows: number[] = [];
    for (let r = 0; r < lay.rows.length && lay.rows[r].header; r++) headerRows.push(r);
    const headerH = headerRows.reduce((s, r) => s + lay.rows[r].height, 0);
    this.prevPara = undefined;
    let ri = 0;
    while (ri < lay.rows.length) {
      const row = lay.rows[ri];
      // rows merged vertically move together
      let blockH = row.height;
      let blockRows = 1;
      const spanMax = Math.max(1, ...row.cells.map((c) => c.rowSpan));
      for (let k = 1; k < spanMax && ri + k < lay.rows.length; k++) { blockH += lay.rows[ri + k].height; blockRows++; }
      if (blockH > pageH - (ri > 0 ? headerH : 0) + EPS) {
        // A row taller than a page cannot be drawn as a unit: write its cells' content as plain paragraphs.
        for (let k = 0; k < blockRows; k++) this.flattenRow(t, ri + k);
        ri += blockRows;
        continue;
      }
      if (blockH > this.remaining() + EPS) {
        if (!this.atTop) {
          this.newPage(this.sec);
          if (headerRows.length && ri > headerRows.length - 1 && !headerRows.includes(ri)) this.drawHeaderRows(lay, headerRows);
        }
      } else if (this.atTop && this.pageNo > 1 && ri > 0 && headerRows.length && !headerRows.includes(ri) && this.y === this.top) {
        this.drawHeaderRows(lay, headerRows);
      }
      this.atTop = false;
      for (let k = 0; k < blockRows; k++) {
        this.drawRow(this.cur.page, this.sec.heightPt, this.cur.links, lay, ri + k, this.sec.marginLeft, this.y, lay.rows.slice(ri + k + 1));
        this.y += lay.rows[ri + k].height;
      }
      ri += blockRows;
    }
  }

  drawHeaderRows(lay: TableLayout, headerRows: number[]) {
    for (const r of headerRows) {
      this.drawRow(this.cur.page, this.sec.heightPt, this.cur.links, lay, r, this.sec.marginLeft, this.y, lay.rows.slice(r + 1));
      this.y += lay.rows[r].height;
    }
    this.atTop = false;
  }

  flattenRow(t: Table, ri: number) {
    for (const cell of t.rows[ri].cells) for (const b of cell.blocks) this.placeBlock(b, undefined);
  }

  /** Whether the next block starts, so keep-with-next can look ahead. */
  placeBlock(b: Block, next: Block | undefined) {
    this.alive();
    if (b.kind === "p") {
      if (b.props.keepNext && next && !this.atTop) {
        const w = this.contentWidth();
        const self = this.layoutPara(b, w, this.pageNo);
        const selfH = self.lines.reduce((s, ln) => s + ln.height, 0) + self.before + self.after;
        let nextH = 0;
        if (next.kind === "p") {
          const nl = this.layoutPara(next, w, this.pageNo);
          nextH = nl.before + nl.lines.slice(0, 2).reduce((s, ln) => s + ln.height, 0);
        } else {
          const tl = this.layoutTable(next, w, this.pageNo);
          nextH = tl.rows[0]?.height ?? 0;
        }
        if (selfH + nextH > this.remaining() + EPS && selfH + nextH <= this.bottom - this.top) this.newPage(this.sec);
      }
      this.placePara(b);
    } else this.placeTable(b);
  }

  // ---- whole document -----------------------------------------------------------------------

  async embedImages() {
    const all: Inline[] = [];
    for (const s of this.doc.sections) {
      collectInlines(s.blocks, all);
      for (const part of [s.header, s.headerFirst, s.footer, s.footerFirst]) if (part) collectInlines(part, all);
    }
    for (const f of this.doc.footnotes) collectInlines(f.blocks, all);
    for (const i of all) {
      if (i.t !== "image" || this.images.has(i.data)) continue;
      try {
        this.images.set(i.data, i.type === "png" ? await this.pdf.embedPng(i.data) : await this.pdf.embedJpg(i.data));
      } catch {
        this.imageFailures++;
      }
    }
  }

  allText(): string {
    const parts: string[] = [];
    for (const s of this.doc.sections) {
      parts.push(blocksText(s.blocks));
      for (const part of [s.header, s.headerFirst, s.footer, s.footerFirst]) if (part) parts.push(blocksText(part));
    }
    for (const f of this.doc.footnotes) parts.push(blocksText(f.blocks));
    return parts.join("\n");
  }

  /** Runs the layout once. When `draw` is false the pages are still created (to count them). */
  runBody() {
    this.pages = [];
    this.pageNo = 0;
    this.sectionFirstPage.clear();
    this.prevPara = undefined;
    const blocksAll: { section: Section; blocks: Block[] }[] = this.doc.sections.map((s) => ({ section: s, blocks: s.blocks }));
    // footnotes follow the last section's content
    if (this.doc.footnotes.length) {
      const last = this.doc.sections[this.doc.sections.length - 1];
      const extra: Block[] = [{ kind: "p", inlines: [], props: { ...emptyProps(), borders: { top: { style: "single", sizePt: 0.5, color: "808080" } }, afterPt: 4, beforePt: 12 }, base: { sizePt: 4 } }];
      for (const f of this.doc.footnotes) {
        f.blocks.forEach((b, bi) => {
          if (b.kind === "p" && bi === 0) extra.push({ ...b, inlines: [{ t: "text", text: `${f.label} `, p: { sizePt: 8, vert: "sup" } }, ...b.inlines], props: { ...b.props, afterPt: Math.min(b.props.afterPt, 2) } });
          else extra.push(b);
        });
      }
      blocksAll[blocksAll.length - 1] = { section: last, blocks: [...last.blocks, ...extra] };
    }
    blocksAll.forEach(({ section, blocks }, si) => {
      const prev = si > 0 ? this.doc.sections[si - 1] : undefined;
      const continuous = section.breakType === "continuous" && prev && prev.widthPt === section.widthPt && prev.heightPt === section.heightPt && this.pages.length > 0;
      if (!continuous) {
        this.newPage(section);
        this.sectionFirstPage.set(section, this.pageNo);
        this.pagesOfSection = 0;
        // re-measure with the first-page header when this section has a title page
        if (section.titlePg) {
          const b = this.bodyBounds(section, true);
          this.top = b.top; this.bottom = section.heightPt - b.bottom; this.y = this.top;
        }
      } else {
        this.sec = section;
        this.cur = { ...this.cur, section };
      }
      blocks.forEach((b, bi) => {
        this.placeBlock(b, blocks[bi + 1]);
        this.pagesOfSection++;
      });
      this.hooks.progress?.((si + 1) / blocksAll.length);
    });
  }

  async build(): Promise<DocxPdfResult> {
    this.pdf = await PDFDocument.create();
    this.fonts = new PdfFontSet(this.pdf, this.opts.fallbackLoader, this.opts.rasterEnv);
    await this.fonts.prepare(this.allText());
    await this.embedImages();
    this.alive();

    // Pass 1 counts the pages (for NUMPAGES); pass 2 draws on a fresh document.
    this.runBody();
    this.total = Math.max(1, this.pages.length);
    const counted = this.total;
    this.pdf = await PDFDocument.create();
    this.fonts = new PdfFontSet(this.pdf, this.opts.fallbackLoader, this.opts.rasterEnv);
    await this.fonts.prepare(this.allText());
    this.fonts.scan(this.allText());
    this.images.clear();
    await this.embedImages();
    this.runBody();
    if (this.pages.length !== counted) this.total = this.pages.length;

    // headers, footers and links
    for (const pg of this.pages) {
      const { header, footer } = this.headerLayouts(pg.section, pg.index);
      const x = pg.section.marginLeft;
      let y = pg.section.headerDist;
      for (const l of header) { this.drawLayout(pg.page, pg.section.heightPt, pg.links, l, x, y); y += this.heightOf(l, true); }
      const fH = footer.reduce((s, l) => s + this.heightOf(l, true), 0);
      let fy = pg.section.heightPt - pg.section.footerDist - fH;
      for (const l of footer) { this.drawLayout(pg.page, pg.section.heightPt, pg.links, l, x, fy); fy += this.heightOf(l, true); }
      for (const k of pg.links) {
        const ctx = this.pdf.context;
        const annot = ctx.obj({ Type: "Annot", Subtype: "Link", Rect: [k.x, k.y, k.x + k.w, k.y + k.h], Border: [0, 0, 0], A: { S: PDFName.of("URI"), URI: PDFString.of(k.url) } });
        pg.page.node.addAnnot(ctx.register(annot));
      }
    }
    stampOutputMetadata(this.pdf, { title: this.doc.title, author: this.doc.author, fallbackTitle: this.opts.fallbackTitle });
    const warnings: string[] = [];
    const rc = replacedCharsMessage(this.fonts);
    if (rc) warnings.push(rc);
    const w = this.doc.warnings;
    if (w.textBoxes) warnings.push(`${w.textBoxes} text ${w.textBoxes === 1 ? "box was" : "boxes were"} not included.`);
    if (w.charts) warnings.push(`${w.charts} ${w.charts === 1 ? "chart was" : "charts were"} not included.`);
    if (w.pictures + this.imageFailures) warnings.push(`${w.pictures + this.imageFailures} ${w.pictures + this.imageFailures === 1 ? "picture" : "pictures"} could not be included.`);
    if (w.columnSections) warnings.push(`${w.columnSections} ${w.columnSections === 1 ? "section is" : "sections are"} set in columns in Word and shown here as a single column.`);
    await this.fonts.flushRasters();
    const bytes = await this.pdf.save();
    return { bytes, pages: this.pages.length, warnings, replacedChars: this.fonts.replacedCount };
  }
}

function emptyProps(): ParaProps {
  return { align: "left", beforePt: 0, afterPt: 0, line: { value: 1, rule: "auto" }, leftPt: 0, rightPt: 0, firstPt: 0, keepNext: false, keepLines: false, pageBreakBefore: false, contextualSpacing: false, widowControl: true, borders: {}, tabs: [] };
}

export async function convertDocxDocumentToPdf(doc: DocxDoc, opts: DocxPdfOptions, hooks: DocxPdfHooks = {}): Promise<DocxPdfResult> {
  return new Writer(doc, opts, hooks).build();
}
